<template>
  <div
    :id="`pf_${facet.field}`"
    class="nosto-facet"
    js-nosto-facet="facetMobile"
  >
    <button
      class="nosto-facet__control text-heading-xs text-heading-2xs-desktop"
      type="button"
      @click="toggleActiveControl"
    >
      <span>
        {{ facet.name }}
        <template v-if="selectedCount > 0 && !isControlActiveProp && !isMobile">
          ({{ selectedCount }})
        </template>
      </span>
      <icon-chevron-right />
      <icon-chevron-down />
    </button>

    <div
      class="nosto-facet__content"
      js-body-scroll-lock-ignore
    >
      <button
        class="nosto-facet__values-header"
        type="button"
        @click="toggleActiveControl"
      >
        <icon-chevron-left />

        <p
          class="nosto-facet__values-title text-heading-l"
          v-text="facet.name"
        />

        <div class="nosto-facet__values-close" />
      </button>

      <!-- Facet name and selected summary above options/search -->
      <div
        v-if="values.length && !isMobile"
        class="nosto-facet__summary"
      >
        <div class="nosto-facet__summary-row">
          <template v-if="selectedCount === 0">
            <span
              class="nosto-facet__summary-title text-body-subtitle-s-desktop"
            >
              {{ $string('collection.select') + ' ' + facet.name.toLowerCase() }}
            </span>
          </template>
          <template v-else>
            <span class="nosto-facet__selected-count">
              {{ selectedCount }} {{ $string('collection.selected') }}
            </span>
            <button
              class="nosto-facet__clear-all button__lable button--text"
              type="button"
              @click.prevent="clearAll"
              v-text="$string('collection.clear_all')"
            />
          </template>
        </div>
      </div>

      <!-- Brands filter -->
      <div
        v-if="facet.field === 'brand'"
        class="nosto-facet__brand-filter"
      >
        <icon-search />

        <input
          v-model="brandFilter"
          class="nosto-facet__brand-filter-input text-body-s-desktop"
          :placeholder="$string('collection.search_brands')"
          type="text"
        >
      </div>

      <!-- When display types are other than range -->
      <ul
        v-if="values.length"
        class="nosto-facet__content-inner"
      >
        <nosto-facet-value
          v-for="value of values"
          :id="value.value"
          :key="value.value"
          class="nosto-facet__value"
          :facet="facet"
          selector="valueMobile"
          :value="value"
          @click="toggleActiveValue"
        />
      </ul>

      <!-- When display type is range -->
      <nosto-range-slider
        v-else-if="facet.type === 'stats'"
        :facet="facet"
        :max="facet?.max"
        :min="facet?.min"
        :choices="choices"
        @nosto-range-updated="updateRangeResults"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Nosto facet (nosto-drawer)
 * -----------------------------------------------------------------------------
 * Nosto filter facet for accordion content.
 *
 * @param {String} choices - Available choices.
 * @param {Object} facet - Component facet.
 * @param {Boolean} isActive - Drawer is active.
 *
 */
import NostoFacetValue from '~async/nosto-facet-value/nosto-facet-value'
import NostoRangeSlider from '~async/nosto-range-slider/nosto-range-slider'
import { mapActions, mapGetters, mapState } from 'vuex'

import IconChevronDown from '~icons/direction/chevron/down.svg'
import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'
import IconSearch from '~icons/misc/search.svg'

export default {
  name: 'NostoFacet',

  components: {
    NostoFacetValue,
    NostoRangeSlider,
    IconChevronDown,
    IconChevronLeft,
    IconChevronRight,
    IconSearch,
  },

  props: {
    choices: {
      type: String,
      default: '',
    },
    facet: {
      type: Object,
      default: null,
    },
    isActive: {
      type: Boolean,
      default: true,
    },
    isControlActiveProp: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'object',
    'facets-ready': () => true,
    'update:isControlActiveProp': (value) => typeof value === 'boolean',
  },

  data() {
    return {
      brandFilter: '',
      sizeOrder: [
        '2 Yrs',
        '3 Yrs',
        '4 Yrs',
        '5 Yrs',
        '6 Yrs',
        '7 Yrs',
        '8 Yrs',
        '10 Yrs',
        '12 Yrs',
        '14 Yrs',
        '16 Yrs',
        'One Size',
      ],
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getChoices: 'main-collection/getChoices',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute formatted brand filter.
     * @returns {String}
     */
    formattedBrandFilter() {
      return this.brandFilter
        .toLowerCase()
        .replaceAll(' ', '')
    },

    /**
     * Compute order of facet values alphabetically, or in correct size order.
     * @returns {Array}
     */
    values() {
      const toSort = this.facet

      if (!toSort?.data?.length) {
        return []
      }

      let values = toSort.data.sort((a, b) => {
        if (a.value > b.value) {
          return 1
        }

        if (b.value > a.value) {
          return -1
        }

        return 0
      })

      if (toSort.name === 'Size') {
        const sortedByNumber = toSort.data.sort((a, b) => a.value - b.value)

        values = sortedByNumber.sort((a, b) => {
          return this.sizeOrder.indexOf(a.key) - this.sizeOrder.indexOf(b.key)
        })
      }

      /**
       * Filter by brand filter if filled.
       */
      values = values.filter((value) => {
        const valueKey = value.value
          .toLowerCase()
          .replaceAll(' ', '')

        return valueKey.includes(this.formattedBrandFilter)
      })

      /**
       * Sort by manual order if set in theme settings.
       */
      if (
        toSort.name === 'Size' &&
        cnvs.settings.sizeFilterOrder?.length
      ) {
        const manualOrders = cnvs.settings.sizeFilterOrder.split(',')
          .map((size) => size.toLowerCase().trim())
        values = values.sort((a, b) => {
          const indexOfA = manualOrders.indexOf(a.value.toLowerCase())
          const indexOfB = manualOrders.indexOf(b.value.toLowerCase())

          /**
           * Neither is in the order array; retain their relative positions
           */
          if (indexOfA === -1 && indexOfB === -1) {
            return 0
          }

          /**
           * Only b is in the order array; place b first
           */
          if (indexOfA === -1) {
            return 1
          }

          /**
           * Only a is in the order array; place a first
           */
          if (indexOfB === -1) {
            return -1
          }

          /**
           * Both are in the order array; order by their positions
           */
          return indexOfA - indexOfB
        })
      }

      return values
    },

    /**
     * Count of selected values for this facet.
     * @returns {Number}
     */
    selectedCount() {
      if (!this.facet?.data) {
        return 0
      }
      return this.facet.data.filter(value => value.selected).length
    },
  },

  watch: {

    /**
     * Watch choices change to emit event.
     */
    choices() {
      this.$emit('facets-ready')
    },

    /**
     * Watch facet change to emit event.
     */
    facet() {
      this.$emit('facets-ready')
    },

    /**
     * Watch is active to clear filter.
     */
    isActive() {
      this.brandFilter = ''
    },
  },

  mounted() {
    this.$emit('facets-ready')
  },

  methods: {

    /**
     * Map Vuex Actions
     */
    ...mapActions({
      updateChoices: 'main-collection/updateChoices',
    }),

    /**
     * Format option for request for 'pf' filter types
     * @param {String} option - Filter option id.
     * @returns {String}
     */
    formatFilterOption(option) {
      let filterOptionId = option.replaceAll('.', '__')

      if (option.includes('pf_')) {
        filterOptionId = `${filterOptionId}%5B%5D`
      }

      return filterOptionId
    },

    /**
     * Toggle active class on facet click.
     * @param {Event} event - Click event.
     */
    toggleActiveControl(event) {
      const controls = document.querySelectorAll('[js-nosto-facet="facetMobile"]')

      controls.forEach((control) => {
        if (control === event.currentTarget.parentNode) {
          const wasActive = control.classList.contains(this.$class('active'))
          control.classList.toggle(this.$class('active'))
          this.$emit('update:isControlActiveProp', !wasActive)
          return
        }

        control.classList.remove(this.$class('active'))
        if (control === this.$el) {
          this.$emit('update:isControlActiveProp', false)
        }
      })
    },

    /**
     * Toggle active class on checkbox click.
     * @param {Event} event - Click event.
     */
    toggleActiveValue(event) {
      const valueElement = event.currentTarget
      const filterOptionId = this.formatFilterOption(valueElement.dataset.filterId)
      const requestParam = `&${filterOptionId}=${valueElement.id}`

      if (valueElement.classList.contains(this.$class('active'))) {
        valueElement.classList.remove(this.$class('active'))

        this.$emit('choices-updated', {
          requestParam,
          action: 'remove',
        })

        return
      }

      valueElement.classList.add(this.$class('active'))

      this.$emit('choices-updated', {
        requestParam,
        action: 'add',
      })
    },

    /**
     * Updates user choices after the range slider is edited by a user.
     * @param {Event} value - Object with data about the new input values.
     */
    updateRangeResults(value) {
      clearInterval(this.timeout)
      this.timeout = null

      if (!this.timeout) {
        this.timeout = setTimeout(() => {
          const values = `${value.min}:${value.max}`
          const requestParam = `&${value.field}[]=${encodeURIComponent(values)}`
          this.$emit(
            'choices-updated',
            {
              action: 'update',
              requestParam,
              text: `${this.$variable('store.currencySymbol')}${value.min} - ${this.$variable('store.currencySymbol')}${value.max}`,
            },
          )
        }, this.$timing('slow'))
      }
    },

    /**
     *
     * @param {type} paramName - [Description of the parameter.]
     * @returns {type} [Description of the return value.]
     */
    clearAll() {
      const facetField = this.facet.field.replaceAll('.', '__')
      const keyPattern = `pf_${facetField}[]`
      const params = new URLSearchParams(this.getChoices)
      const newParams = new URLSearchParams()
      for (const [key, value] of params.entries()) {
        if (key !== keyPattern) {
          newParams.append(key, value)
        }
      }

      const newChoices = newParams.toString()
        .replaceAll('+', ' ')
        .replace('%5B%5D', '[]')
        .replace('%5b%5d', '[]')
        .replace('%253A', '%3A')
        .replace('%253a', '%3A')

      this.updateChoices(newChoices)
    },
  },
}
</script>

<style lang="scss">
@import './nosto-facet';
</style>
